// cSpell:ignore unticked verrekend
// Provision days an earlier confirmed settlement already counted.
//
// Each month of provision is settled once (Lewis, 2026-10-05). The months a
// settlement used are frozen per lease, but a lease does not always live as
// long as the tenant: one ended early and re-created, or deleted and entered
// again to fix a typo, is a new lease for the same tenant in the same home.
// Matched by lease alone, the new lease started with nothing used and the
// tenant was refunded the same months a second time, at the owner's expense.
// A new lease is matched by the people on it, not by its payer: the payer is
// whoever pays the rent or the one the broker picked, which can be another
// of the same tenants on the new lease.
//
// The ledger key is (slice, lease) across the whole property tree (Lewis,
// 2026-10-16): a building settlement and a settlement of one of its units
// may each count the same month of a lease, for another slice of its
// provision, but never twice for the same slice.

import type { IDepartureCorrection } from "./departureCorrection.utils";
import {
  contractProvisionSlices,
  Interval,
  IOpenProvisionLine,
  legacyProvisionIntervalsByContract,
  netIntervals,
  SliceIntervals,
  subtractIntervals,
  unionIntervals,
} from "./engine";
import { IPayerCandidate } from "./payer.utils";
import { ProvisionSlice } from "./provisionHistory";
import {
  ContractRef,
  Invoice,
  isDepartureSettlement,
  PerContractResult,
  Settlement,
  SettlementScope,
  SettlementSnapshot,
} from "./types";

const ALL_SLICES: ProvisionSlice[] = ["common", "private"];

// The slices of the provision a settlement frozen before the slices were
// recorded (SettlementSnapshot.provisionSlices) weighed, as the wizard chose
// them then: a group its common slice, a unit inside a group its private
// slice, a standalone unit and a departure (which settles its lease's group
// and own costs at once) both. Mirrored in rentio-api-next
// (getLegacyProvisionSlices). `unitHasParent` reads the unit's group today.
export const legacyProvisionSlices = (params: {
  scope: SettlementScope;
  isDeparture: boolean;
  unitHasParent: boolean;
}): ProvisionSlice[] => {
  const { scope, isDeparture, unitHasParent } = params;
  if (isDeparture) return ALL_SLICES;
  if (scope === "group") return ["common"];
  return unitHasParent ? ["private"] : ALL_SLICES;
};

// The slices of the provision a confirmed settlement weighed: those it
// recorded, else the ones legacyProvisionSlices infers.
export const settlementProvisionSlices = (params: {
  snapshot: Pick<SettlementSnapshot, "provisionSlices">;
  scope: SettlementScope;
  isDeparture: boolean;
  unitHasParent: boolean;
}): ProvisionSlice[] =>
  params.snapshot.provisionSlices ?? legacyProvisionSlices(params);

// The days per slice one row of a confirmed settlement counted: its ledger
// record, else (an older snapshot) its days for each slice the settlement
// weighed. A snapshot older still froze no days at all: `derived` re-derives
// them from its costs and period, by the rule of its day
// (legacyProvisionIntervalsByContract).
export const settledIntervalsBySlice = (params: {
  row: Pick<
    PerContractResult,
    "provisionIntervals" | "provisionIntervalsBySlice"
  >;
  slices: ProvisionSlice[];
  derived?: Interval[];
}): SliceIntervals => {
  const { row, slices, derived } = params;
  if (row.provisionIntervalsBySlice) return row.provisionIntervalsBySlice;
  const intervals = row.provisionIntervals ?? derived ?? [];
  return Object.fromEntries(slices.map(slice => [slice, intervals]));
};

// The accounts of the people on a lease: its tenants and its parents or
// guardians. Someone who only pays the rent is left out, since they may pay
// for the next tenant of the same home too. A lease without its members
// known falls back to its payer.
export const leaseAccountIds = (lease: {
  payerCandidates?: IPayerCandidate[];
  tenantAccountId?: number;
}): number[] => {
  const people = (lease.payerCandidates ?? [])
    .filter(candidate => candidate.role !== "other")
    .map(candidate => candidate.accountId);
  if (people.length > 0) return people;
  return lease.tenantAccountId != null ? [lease.tenantAccountId] : [];
};

// The provision days one lease used in one earlier confirmed settlement.
export interface ISettledProvisionEntry {
  contractId: string;
  unitId: string;
  // The people on that lease (see leaseAccountIds); empty when not known,
  // which then matches nothing but the lease itself.
  accountIds: number[];
  settlementName: string;
  // Per slice of the provision, the days it counted (see
  // PerContractResult.provisionIntervalsBySlice).
  intervalsBySlice: SliceIntervals;
}

// Days an earlier lease of the same people in the same unit already used.
export interface IEarlierLeaseProvision {
  settlementName: string;
  // Of the slices the current lease weighs.
  intervals: Interval[];
}

// The days of one slice that one earlier settlement already counted for a
// lease, by that settlement's name.
export interface ISettledSliceSource {
  settlementName: string;
  intervals: Interval[];
}

export interface ISettledProvisionsByLease {
  // Per lease and per slice: the days it may not count any more, its own and
  // those of an earlier lease of (one of) the same people in the same unit.
  byContract: Record<string, SliceIntervals>;
  // Per current lease: what an earlier lease of that tenant already used, so
  // the wizard can say why those months drop out.
  fromEarlierLeases: Record<string, IEarlierLeaseProvision[]>;
  // Per lease and per slice: which earlier settlements counted those days,
  // so the provisions step can say where a slice's months went ("al
  // verrekend in …").
  settledBy: Record<
    string,
    Partial<Record<ProvisionSlice, ISettledSliceSource[]>>
  >;
  // Per lease: the provision lines an earlier settlement left out of a
  // slice it counted, with the days still open for each (decision 14).
  openLines: Record<string, IOpenProvisionLine[]>;
}

// One row's line ledger, as a confirmed settlement froze it (see
// PerContractResult.provisionLinesLeftOut / provisionLineIntervals).
export interface ISettledLineEntry {
  contractId: string;
  settlementName: string;
  // Per slice, the days it counted (its ledger record).
  intervalsBySlice: SliceIntervals;
  leftOutBySlice?: Partial<Record<ProvisionSlice, string[]>>;
  lineIntervals?: Record<string, Interval[]>;
}

// The lines still open per lease: every line a settlement left out, for the
// days that settlement counted its slice, minus the days a later settlement
// counted for that line alone. A line is settled once per day, like a slice.
export const openProvisionLines = (
  entries: ISettledLineEntry[],
): Record<string, IOpenProvisionLine[]> => {
  const leftOut: Record<
    string,
    Record<string, { slice: ProvisionSlice; days: Interval[]; names: string[] }>
  > = {};
  const counted: Record<string, Record<string, Interval[]>> = {};
  for (const entry of entries) {
    for (const [slice, keys] of Object.entries(
      entry.leftOutBySlice ?? {},
    ) as Array<[ProvisionSlice, string[]]>) {
      const days = entry.intervalsBySlice[slice] ?? [];
      if (days.length === 0) continue;
      for (const lineKey of keys) {
        const byLine = (leftOut[entry.contractId] ??= {});
        const open = (byLine[lineKey] ??= { slice, days: [], names: [] });
        open.days.push(...days);
        if (!open.names.includes(entry.settlementName)) {
          open.names.push(entry.settlementName);
        }
      }
    }
    for (const [lineKey, days] of Object.entries(entry.lineIntervals ?? {})) {
      const byLine = (counted[entry.contractId] ??= {});
      byLine[lineKey] = [...(byLine[lineKey] ?? []), ...days];
    }
  }
  const out: Record<string, IOpenProvisionLine[]> = {};
  for (const [contractId, byLine] of Object.entries(leftOut)) {
    for (const [lineKey, open] of Object.entries(byLine)) {
      const intervals = subtractIntervals(
        unionIntervals([open.days]),
        counted[contractId]?.[lineKey] ?? [],
      );
      if (intervals.length === 0) continue;
      (out[contractId] ??= []).push({
        lineKey,
        slice: open.slice,
        intervals,
        settlementNames: open.names,
      });
    }
  }
  return out;
};

// Adds what `entry` counted to a lease's sources, slice by slice.
const addSources = (
  target: Partial<Record<ProvisionSlice, ISettledSliceSource[]>> | undefined,
  entry: Pick<ISettledProvisionEntry, "settlementName" | "intervalsBySlice">,
): Partial<Record<ProvisionSlice, ISettledSliceSource[]>> => {
  const out = { ...target };
  for (const [slice, intervals] of Object.entries(
    entry.intervalsBySlice,
  ) as Array<[ProvisionSlice, Interval[]]>) {
    if (intervals.length === 0) continue;
    out[slice] = [
      ...(out[slice] ?? []),
      { settlementName: entry.settlementName, intervals },
    ];
  }
  return out;
};

// Adds every day of `entry` to `target`, slice by slice.
const addSlices = (
  target: SliceIntervals | undefined,
  entry: SliceIntervals,
): SliceIntervals => {
  const out: SliceIntervals = { ...target };
  for (const [slice, intervals] of Object.entries(entry) as Array<
    [ProvisionSlice, Interval[]]
  >) {
    out[slice] = [...(out[slice] ?? []), ...intervals];
  }
  return out;
};

// A correction of a lease whose end moved (decision 3), as the ledger reads
// it: the provision days it credited count as settled, those it took back
// are open again (M05).
export interface ISettledCorrectionEntry {
  settlementName: string;
  correction: Pick<
    IDepartureCorrection,
    "contractId" | "provisionCreditedBySlice" | "provisionTakenBackBySlice"
  >;
}

export const settledProvisionsByLease = (params: {
  entries: ISettledProvisionEntry[];
  contracts: ContractRef[];
  lineEntries?: ISettledLineEntry[];
  corrections?: ISettledCorrectionEntry[];
}): ISettledProvisionsByLease => {
  const { entries, contracts } = params;
  const byContract: Record<string, SliceIntervals> = {};
  const fromEarlierLeases: Record<string, IEarlierLeaseProvision[]> = {};
  const settledBy: ISettledProvisionsByLease["settledBy"] = {};
  for (const entry of entries) {
    byContract[entry.contractId] = addSlices(
      byContract[entry.contractId],
      entry.intervalsBySlice,
    );
    settledBy[entry.contractId] = addSources(
      settledBy[entry.contractId],
      entry,
    );
  }
  for (const contract of contracts) {
    const people = leaseAccountIds(contract);
    if (people.length === 0) continue;
    const slices = contractProvisionSlices(contract);
    for (const entry of entries) {
      if (entry.contractId === contract.id) continue;
      if (entry.unitId !== String(contract.unitId)) continue;
      if (!entry.accountIds.some(id => people.includes(id))) continue;
      // Only the slices this lease weighs: what the earlier lease used of
      // another slice takes nothing off here.
      const intervals = unionIntervals(
        slices.map(slice => entry.intervalsBySlice[slice] ?? []),
      );
      if (intervals.length === 0) continue;
      byContract[contract.id] = addSlices(
        byContract[contract.id],
        entry.intervalsBySlice,
      );
      settledBy[contract.id] = addSources(settledBy[contract.id], entry);
      fromEarlierLeases[contract.id] = [
        ...(fromEarlierLeases[contract.id] ?? []),
        { settlementName: entry.settlementName, intervals },
      ];
    }
  }
  // Per lease, what corrections credited and took back: a day counts as
  // settled while more settlements counted or credited it than took it back.
  const takenBack: Record<string, SliceIntervals> = {};
  for (const { settlementName, correction } of params.corrections ?? []) {
    const { contractId } = correction;
    const credited = correction.provisionCreditedBySlice ?? {};
    byContract[contractId] = addSlices(byContract[contractId], credited);
    settledBy[contractId] = addSources(settledBy[contractId], {
      settlementName,
      intervalsBySlice: credited,
    });
    takenBack[contractId] = addSlices(
      takenBack[contractId],
      correction.provisionTakenBackBySlice ?? {},
    );
  }
  for (const [contractId, bySlice] of Object.entries(takenBack)) {
    const own = byContract[contractId] ?? {};
    for (const [slice, intervals] of Object.entries(bySlice) as Array<
      [ProvisionSlice, Interval[]]
    >) {
      own[slice] = netIntervals(own[slice] ?? [], intervals);
    }
    byContract[contractId] = own;
  }
  return {
    byContract,
    fromEarlierLeases,
    settledBy,
    openLines: openProvisionLines(params.lineEntries ?? []),
  };
};

// What the settlements already confirmed in the property tree took off the
// leases of a new settlement, per slice (see settledProvisionsByLease). The
// list holds the settled property, its group and its units (useSettlements):
// a unit settlement in a group's list is one of its units, in a unit's list
// that unit itself. Every confirmed settlement but `ownId` counts, for the
// slices it weighed — a group's common slice leaves a unit's private slice
// open, and a departure took both. Older snapshots infer their slices and,
// without frozen days, re-derive them from their costs and period.
export const settledProvisionsInTree = (params: {
  settlements: Settlement[];
  // The settlement being made: never counted against itself.
  ownId?: string;
  scope: SettlementScope;
  // The settled unit has a group (unit scope only).
  unitInGroup: boolean;
  contracts: ContractRef[];
  // The costs the wizard knows, to re-derive the days of the oldest
  // snapshots.
  invoices: Invoice[];
  nameOf: (settlement: Settlement) => string;
}): ISettledProvisionsByLease => {
  const { settlements, ownId, scope, unitInGroup, contracts, invoices } =
    params;
  const entries: ISettledProvisionEntry[] = [];
  const lineEntries: ISettledLineEntry[] = [];
  const corrections: ISettledCorrectionEntry[] = [];
  for (const s of settlements) {
    if (s.status !== "confirmed" || s.id === ownId || !s.snapshot) continue;
    for (const correction of s.snapshot.departureCorrections ?? []) {
      corrections.push({ settlementName: params.nameOf(s), correction });
    }
    const slices = settlementProvisionSlices({
      snapshot: s.snapshot,
      scope: s.scope,
      isDeparture: isDepartureSettlement(s),
      unitHasParent: s.scope === "unit" && (scope === "group" || unitInGroup),
    });
    const ids = new Set(s.snapshot.invoiceIds);
    const derived = legacyProvisionIntervalsByContract(
      contracts,
      invoices.filter(i => ids.has(i.id)),
      { from: s.periodFrom, to: s.periodTo },
    );
    const settlementName = params.nameOf(s);
    for (const pc of s.snapshot.perContract ?? []) {
      // The frozen ledger decides, whatever the amount: a slice typed in
      // as € 0 still settled its months (N02). The engine froze no day for
      // an opted-out lease or an unticked slice. Only a row older than the
      // ledger falls back to its amount.
      const hasLedger =
        pc.provisionIntervalsBySlice != null || pc.provisionIntervals != null;
      if (!hasLedger && !(pc.provision > 0)) continue;
      // Older snapshots froze no people: read them from the lease while it
      // still exists, else take the payer they froze.
      const lease = contracts.find(c => c.id === pc.contractId);
      const frozenPayer = pc.payerAccountId != null ? [pc.payerAccountId] : [];
      const intervalsBySlice = settledIntervalsBySlice({
        row: pc,
        slices,
        derived: derived[pc.contractId],
      });
      entries.push({
        contractId: pc.contractId,
        unitId: String(pc.unitId),
        accountIds:
          pc.leaseAccountIds ?? (lease ? leaseAccountIds(lease) : frozenPayer),
        settlementName,
        intervalsBySlice,
      });
      if (pc.provisionLinesLeftOut || pc.provisionLineIntervals) {
        lineEntries.push({
          contractId: pc.contractId,
          settlementName,
          intervalsBySlice,
          leftOutBySlice: pc.provisionLinesLeftOut,
          lineIntervals: pc.provisionLineIntervals,
        });
      }
    }
  }
  return settledProvisionsByLease({
    entries,
    contracts,
    lineEntries,
    corrections,
  });
};

export interface IEarlierLeaseHint {
  contract: ContractRef;
  settlementNames: string[];
  // The days of this settlement that drop out for it.
  intervals: Interval[];
}

// The days of `intervals` that `other` covers too.
export const sharedDays = (
  intervals: Interval[],
  other: Interval[],
): Interval[] =>
  subtractIntervals(intervals, subtractIntervals(intervals, other));

// The leases of this settlement that lose provision days because an earlier
// lease of the same people in the same unit already counted them, with those
// days. `intervals` holds each lease's days before anything was taken off.
export const earlierLeaseHints = (params: {
  contracts: ContractRef[];
  intervals: Record<string, Interval[]>;
  fromEarlierLeases: Record<string, IEarlierLeaseProvision[]>;
}): IEarlierLeaseHint[] =>
  params.contracts.flatMap(contract => {
    const own = params.intervals[contract.id] ?? [];
    const earlier = (params.fromEarlierLeases[contract.id] ?? []).filter(
      entry => sharedDays(own, entry.intervals).length > 0,
    );
    if (earlier.length === 0) return [];
    return [
      {
        contract,
        settlementNames: [
          ...new Set(earlier.map(entry => entry.settlementName)),
        ],
        intervals: sharedDays(
          own,
          earlier.flatMap(entry => entry.intervals),
        ),
      },
    ];
  });

// The tenants of a settlement whose provision days of its period were all
// settled before (M21): they take part with their costs, but the provisions step had
// no row for them, so Bea could not see why the summary gives them no
// provision. With the settlements that settled those days and the days.
export interface IFullySettledProvision {
  contract: ContractRef;
  settlementNames: string[];
  intervals: Interval[];
}

export const fullySettledProvisions = (params: {
  // The leases the provisions step shows no row for.
  hidden: ContractRef[];
  // The leases that take part in the settlement (ComputeResult.perContract).
  partyIds: Set<string>;
  // Per lease, the days of its slices earlier settlements counted.
  sourcesOf: (contract: ContractRef) => ISettledSliceSource[];
}): IFullySettledProvision[] =>
  params.hidden.flatMap(contract => {
    if (!params.partyIds.has(contract.id)) return [];
    const sources = params.sourcesOf(contract);
    if (sources.length === 0) return [];
    return [
      {
        contract,
        settlementNames: [
          ...new Set(sources.map(source => source.settlementName)),
        ],
        intervals: unionIntervals(sources.map(source => source.intervals)),
      },
    ];
  });
